import { useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useContext, useState } from 'react';

import { type AuthTokens, clearTokens, getTokens, isRefreshTokenExpired, saveTokens } from '@/lib/auth-token';
import { router } from '@/router';

interface AuthContextType {
	isAuthenticated: boolean;
	tokens: AuthTokens | null;
	login: (tokens: AuthTokens) => void;
	logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Runs once on mount. Evicts sessions whose refresh token has already expired
// so the guard and isAuthenticated are in sync from the very first render.
// Access-token freshness is not checked here — the request interceptor handles that.
function getInitialTokens(): AuthTokens | null {
	const stored = getTokens();

	if (!stored) return null;

	if (isRefreshTokenExpired(stored)) {
		clearTokens();

		return null;
	}

	return stored;
}

export function AuthProvider({ children }: { children: ReactNode }) {
	const queryClient = useQueryClient();
	const [tokens, setTokens] = useState<AuthTokens | null>(getInitialTokens);

	const login = (newTokens: AuthTokens) => {
		saveTokens(newTokens);
		setTokens(newTokens);
	};

	const logout = () => {
		clearTokens();
		setTokens(null);
		queryClient.clear();
		void router.navigate({ to: '/sign-in' });
	};

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated: !!tokens,
				tokens,
				login,
				logout,
			}}>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = useContext(AuthContext);

	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
}
